import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UserDao } from '@app/dao/user/user.dao';
import { DocumentDao } from '@app/dao/document/document.dao';
import { catchError, take, throwError } from 'rxjs';

import { PrButtonConfirmComponent } from '../pr-button/pr-button-confirmation/pr-button-confirm.component';
import { GedDocumentFileService } from '../pr-ged-picker/ged-document-file.service';
import { GedPickerComponent } from '../pr-ged-picker/ged-picker.component';
import {
  buildGedDocumentTags,
  GedDocumentTag,
  isExpired,
} from '../pr-ged-picker/ged-document-tags';

@Component({
  selector: 'ged-document-card',
  templateUrl: './ged-document-card.component.html',
  styleUrls: ['./ged-document-card.component.scss'],
  standalone: false,
})
export class GedDocumentCardComponent implements OnChanges, OnDestroy {
  @Input() doc: any;

  @Input() accountId: any;

  @Input() documents: string[] = [];

  @Input() ownerId: number;

  @Output() pick = new EventEmitter<any>();

  @Output() withdraw = new EventEmitter<void>();

  @Output() remove = new EventEmitter<void>();

  @ViewChild('gedPickerRef') gedPickerRef: GedPickerComponent;

  @ViewChild('deleteConfirm', { read: PrButtonConfirmComponent })
  deleteConfirm: PrButtonConfirmComponent;

  menuOpen = false;

  downloading = false;

  private tagList: GedDocumentTag[] = [];

  private owners: any[] = [];

  private documentLabels: { [code: string]: string } = {};

  private documentTypeLabels: { [code: string]: string } = {};

  private content: ArrayBuffer;

  constructor(
    private documentDao: DocumentDao,
    private userDao: UserDao,
    private fileService: GedDocumentFileService,
  ) {
    this.documentDao
      .getReferences()
      .pipe(take(1))
      .subscribe(references => {
        this.documentLabels = this.toLabelMap(references?.gedDocuments);
        this.documentTypeLabels = this.toLabelMap(references?.gedDocumentTypes);
      });

    this.userDao
      .getOwners()
      .pipe(take(1))
      .subscribe(owners => {
        this.owners = owners || [];
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.doc) {
      return;
    }

    this.content = null;
    this.refreshTags();
  }

  ngOnDestroy() {
    this.content = null;
  }

  @HostListener('document:click')
  closeMenu() {
    this.menuOpen = false;
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.menuOpen = false;
  }

  toggleMenu(event: Event) {
    event.stopPropagation();
    this.menuOpen = !this.menuOpen;
  }

  get tags(): GedDocumentTag[] {
    return this.tagList;
  }

  get name(): string {
    return this.doc?.label || this.typeLabel || 'Document sans nom';
  }

  get typeLabel(): string {
    return (
      this.documentTypeLabels[this.doc?.documentType] ||
      this.documentLabels[this.doc?.document] ||
      this.doc?.document ||
      ''
    );
  }

  get ownersLabel(): string {
    const labels = (this.doc?.ownerIds || [])
      .map(ownerId => this.owners.find(owner => owner?.id == ownerId)?.label)
      .filter(Boolean);

    return labels.length ? labels.join(', ') : 'Aucun titulaire';
  }

  get fileCountLabel(): string {
    const count = (this.doc?.files || []).length;
    return count ? `${count} fichier${count > 1 ? 's' : ''}` : '';
  }

  get expired(): boolean {
    return isExpired(this.doc);
  }

  get expiryDate(): string {
    return this.formatDate(this.doc?.dateExpired);
  }

  get createdDate(): string {
    return this.formatDate(this.doc?.dateCreated);
  }

  get identityTile(): string {
    return this.doc?.document === 'PASSEPORT' ? 'menu_book' : 'badge';
  }

  openGedPicker() {
    this.menuOpen = false;
    this.gedPickerRef?.open();
  }

  onPick(doc: any) {
    this.pick.emit(doc);
  }

  onWithdraw() {
    this.menuOpen = false;
    this.withdraw.emit();
  }

  askDelete() {
    this.menuOpen = false;
    this.deleteConfirm?.open();
  }

  download() {
    if (!this.doc?.id || !this.accountId || this.downloading) {
      return;
    }

    if (this.content) {
      this.fileService.saveAs(this.content, this.name);
      return;
    }

    this.downloading = true;

    this.fileService
      .download(this.accountId, this.doc.id)
      .pipe(
        catchError(error => {
          this.downloading = false;
          return throwError(error);
        }),
      )
      .subscribe(file => {
        this.downloading = false;
        this.content = file.content;
        this.fileService.revoke([file.objectUrl]);
        this.fileService.saveAs(file.content, this.name);
      });
  }

  private refreshTags() {
    this.tagList = buildGedDocumentTags(this.doc, undefined);
  }

  private formatDate(value: any): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? '' : date.toLocaleDateString('fr-FR');
  }

  private toLabelMap(references: any[]): { [code: string]: string } {
    return (references || []).reduce(
      (acc, reference) => ({ ...acc, [reference?.code]: reference?.label }),
      {},
    );
  }
}
